import { uniqBy, sortBy } from 'lodash'
import { ToastProgrammatic as Toast } from 'buefy'
import * as belts from '~/mixins/belts'
import * as ageDivisions from '~/mixins/ageDivisions'
import * as weightDivisions from '~/mixins/weightDivisions'

const GENDER_MAP = { male: 0, female: 1 }

const getDefaultState = () => ({
  isLoading: false,
  isSubmitting: false,
  registration: null,
  availableTeams: [],
  availableCategories: []
})

export const state = () => getDefaultState()

export const getters = {
  registration(state) {
    return state.registration
  },
  isLoading(state) {
    return state.isLoading
  },
  isSubmitting(state) {
    return state.isSubmitting
  },
  availableTeams(state) {
    return state.availableTeams
  },
  availableBelts(state, _getters, _rootState, rootGetters) {
    if (!state.availableCategories.length) return []

    const athlete = rootGetters['staff/athlete/current']
    const athleteBelt = belts.byNameWithoutDegree(athlete?.belt) ?? null

    const availableBelts = uniqBy(state.availableCategories, 'beltColorId')
      .filter(c => c.beltColorId)
      .map(c => belts.byId(c.beltColorId))

    const isAthleteBeltAvailable = athleteBelt && availableBelts.some(b => b.id === athleteBelt.id)
    const result = isAthleteBeltAvailable ? [{ ...athleteBelt }] : []

    return result
  },
  availableAgeDivisions(state) {
    const reg = state.registration
    if (!reg || !state.availableCategories.length) return []

    const categories = state.availableCategories.filter(c => c.beltColorId === reg.beltId)

    const uniqDivisions = uniqBy(categories, 'ageDivisionId').map(category =>
      ageDivisions.findById(category.ageDivisionId)
    )

    return sortBy(uniqDivisions, 'min')
  },
  availableWeightDivisions(state) {
    const reg = state.registration
    if (!reg || !state.availableCategories.length) return []

    const categories = state.availableCategories.filter(
      c => c.beltColorId === reg.beltId && c.ageDivisionId === reg.ageDivision?.id
    )

    const uniqDivisions = uniqBy(categories, 'weightDivisionId').map(division => {
      return {
        ...weightDivisions.findById(division.weightDivisionId),

        weightLimit: division.weightLimit,
        weightLimitUnit: division.weightLimitUnit
      }
    })

    return sortBy(uniqDivisions, 'id')
  }
}

export const mutations = {
  registration(state, registration) {
    state.registration = registration
  },
  isLoading(state, isLoading) {
    state.isLoading = isLoading
  },
  isSubmitting(state, isSubmitting) {
    state.isSubmitting = isSubmitting
  },
  availableTeams(state, availableTeams) {
    state.availableTeams = availableTeams
  },
  availableCategories(state, availableCategories) {
    state.availableCategories = availableCategories
  },
  reset(state) {
    Object.assign(state, getDefaultState())
  }
}

export const actions = {
  async bootstrap({ commit }, summaryRegistration) {
    commit('reset')
    commit('isLoading', true)

    try {
      const reg = summaryRegistration
      const championshipId = reg.championship.id

      const belt = belts.byName(reg.category.belt)
      const ageDivision = ageDivisions.findByName(reg.category.ageDivision) || null
      const weightDivision = weightDivisions.findByName(reg.category.weightDivision) || null

      const [teamsResponse, categoriesResponse] = await Promise.all([
        this.$coreApi.$get(`staff/championship_registrations/${reg.id}/available_teams`, {
          params: { championshipId }
        }),
        this.$coreApi.$get(
          `staff/championship_registrations/${reg.id}/available_championship_categories`,
          {
            params: { championshipId }
          }
        )
      ])

      const teams = teamsResponse.data
      const categories = categoriesResponse.data

      commit('availableTeams', teams)
      commit('availableCategories', categories)

      const team = teams.find(t => +t.id === +reg.academyTeamId) || null

      commit('registration', {
        id: reg.id,
        athleteId: reg.athleteId,
        championship: {
          id: reg.championship.id,
          name: reg.championship.name
        },
        genderId: GENDER_MAP[reg.category.gender],
        beltId: belt.id,
        belt,
        status: reg.status,
        team,
        ageDivision,
        weightDivision,
        validations: {}
      })
    } catch (error) {
      Toast.open({
        duration: 5000,
        message: this.$formatServerError(error, 'errors'),
        position: 'is-top',
        type: 'is-danger'
      })

      throw error
    } finally {
      commit('isLoading', false)
    }
  },

  updateRegistration({ commit }, registration) {
    commit('registration', registration)
  },

  async submit({ state, commit }) {
    const reg = state.registration
    commit('isSubmitting', true)

    try {
      await this.$coreApi.$post(`staff/championship_registrations/${reg.id}/edit_registration`, {
        championship_registration: {
          id: reg.id,
          academy_team_id: reg.team.id,
          age_division: reg.ageDivision.name,
          weight_division: reg.weightDivision.name,
          belt: reg.belt?.name
        }
      })
    } catch (error) {
      Toast.open({
        duration: 5000,
        message: this.$formatServerError(error, 'errors'),
        position: 'is-top',
        type: 'is-danger'
      })

      throw error
    } finally {
      commit('isSubmitting', false)
    }
  },

  reset({ commit }) {
    commit('reset')
  }
}
